import React, { useEffect, useRef, useState } from 'react';
import { createChart, ColorType, CrosshairMode } from 'lightweight-charts';
import './TradingChart.css';

interface TradingChartProps {
  symbol?: string;
  exchange?: string;
  timeframe?: string;
  width?: number;
  height?: number;
}

const TradingChart: React.FC<TradingChartProps> = ({
  symbol = 'RELIANCE',
  exchange = 'NSE',
  timeframe = '1D',
  width = 800,
  height = 500
}) => {
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<any>(null);
  const [priceData, setPriceData] = useState<any>({
    open: 0,
    high: 0,
    low: 0,
    close: 0,
    prevClose: 0,
  });

  useEffect(() => {
    if (!chartContainerRef.current) return;

    const chart = createChart(chartContainerRef.current, {
      width: 0,
      height: height,
      layout: {
        background: { type: ColorType.Solid, color: 'transparent' },
        textColor: '#d1d4dc',
      },
      grid: {
        vertLines: { color: 'rgba(197, 203, 206, 0.1)' },
        horzLines: { color: 'rgba(197, 203, 206, 0.1)' },
      },
      crosshair: {
        mode: CrosshairMode.Normal,
      },
      rightPriceScale: {
        borderColor: 'rgba(197, 203, 206, 0.3)',
      },
      timeScale: {
        borderColor: 'rgba(197, 203, 206, 0.3)',
        timeVisible: true,
        secondsVisible: false,
        rightOffset: 12,
        barSpacing: 10,
      },
    });

    chartRef.current = chart;

    const candlestickSeries = (chart as any).addCandlestickSeries({
      upColor: '#26a69a',
      downColor: '#ef5350',
      borderVisible: false,
      wickUpColor: '#26a69a',
      wickDownColor: '#ef5350',
    });

    // Sample data
    const sampleData = [
      { time: "2019-04-11", open: 199.9, high: 201.5, low: 199.03, close: 201.48 },
      { time: "2019-04-12", open: 202.13, high: 204.26, low: 202.13, close: 203.85 },
      { time: "2019-04-15", open: 204.16, high: 205.14, low: 203.4, close: 204.86 },
      { time: "2019-04-16", open: 205.25, high: 205.99, low: 206.29, close: 204.90 },
      { time: "2019-04-17", open: 205.34, high: 206.84, low: 205.32, close: 206.55 },
      { time: "2019-04-18", open: 206.02, high: 207.78, low: 205.1, close: 205.66 },
      { time: "2019-04-22", open: 204.11, high: 206.25, low: 204.0, close: 204.78 },
      { time: "2019-04-23", open: 205.14, high: 207.33, low: 203.43, close: 206.05 },
      { time: "2019-04-24", open: 206.16, high: 208.29, low: 205.54, close: 206.72 },
      { time: "2019-04-25", open: 206.01, high: 207.72, low: 205.06, close: 206.5 },
      { time: "2019-04-26", open: 205.88, high: 206.14, low: 203.34, close: 203.61 },
      { time: "2019-04-29", open: 203.31, high: 203.8, low: 200.34, close: 202.16 },
      { time: "2019-04-30", open: 201.55, high: 203.75, low: 200.79, close: 203.7 },
      { time: "2019-05-01", open: 203.2, high: 203.52, low: 198.66, close: 198.8 },
      { time: "2019-05-02", open: 199.3, high: 201.06, low: 198.8, close: 201.01 },
      { time: "2019-05-03", open: 202.0, high: 202.31, low: 200.32, close: 200.56 },
      { time: "2019-05-06", open: 198.74, high: 199.93, low: 198.31, close: 199.63 },
      { time: "2019-05-07", open: 196.75, high: 197.65, low: 192.96, close: 194.77 },
      { time: "2019-05-08", open: 194.49, high: 196.61, low: 193.68, close: 195.17 },
      { time: "2019-05-09", open: 193.31, high: 195.08, low: 191.59, close: 194.58 },
       { time: "2019-05-10", open: 193.31, high: 195.08, low: 191.59, close: 192.58 },
       { time: "2019-05-20", open: 194.31, high: 195.08, low: 191.59, close: 196.58 },
    ];

    candlestickSeries.setData(sampleData);

    // Calculate EMA (Exponential Moving Average)
    const calculateEMA = (data: any[], period: number) => {
      const ema = [];
      const multiplier = 2 / (period + 1);

      // Calculate initial SMA for the first EMA value
      let sum = 0;
      for (let i = 0; i < period && i < data.length; i++) {
        sum += data[i].close;
      }
      let emaValue = sum / period;

      for (let i = period - 1; i < data.length; i++) {
        if (i === period - 1) {
          ema.push({ time: data[i].time, value: emaValue });
        } else {
          emaValue = (data[i].close - emaValue) * multiplier + emaValue;
          ema.push({ time: data[i].time, value: emaValue });
        }
      }

      return ema;
    };

    // Calculate 21 EMA and 44 EMA
    const ema21Data = calculateEMA(sampleData, 21);
    const ema44Data = calculateEMA(sampleData, 44);

    // Add 21 EMA line (Orange color)
    const ema21Series = (chart as any).addLineSeries({
      color: '#FF6B00',
      lineWidth: 1,
      title: 'EMA 21',
      priceLineVisible: false,
      lastValueVisible: false,
    });
    ema21Series.setData(ema21Data);

    // Add 44 EMA line (Blue color)
    const ema44Series = (chart as any).addLineSeries({
      color: '#2962FF',
      lineWidth: 1,
      title: 'EMA 44',
      priceLineVisible: false,
      lastValueVisible: false,
    });
    ema44Series.setData(ema44Data);

    // Set initial price data from last candle
    const lastCandle = sampleData[sampleData.length - 1];
    const prevCandle = sampleData[sampleData.length - 2];
    setPriceData({
      open: lastCandle.open,
      high: lastCandle.high,
      low: lastCandle.low,
      close: lastCandle.close,
      prevClose: prevCandle.close,
    });

    // Update price data on crosshair move
    chart.subscribeCrosshairMove((param: any) => {
      console.log('Crosshair move:', param);

      if (!param || !param.time) {
        // Reset to last candle data
        setPriceData({
          open: lastCandle.open,
          high: lastCandle.high,
          low: lastCandle.low,
          close: lastCandle.close,
          prevClose: prevCandle.close,
        });
        return;
      }

      // Try to get data from seriesData (v4 API)
      if (param.seriesData && param.seriesData.size > 0) {
        const iterator = param.seriesData.values();
        const data = iterator.next().value;

        if (data && data.open !== undefined) {
          console.log('Found data:', data);
          const currentIndex = sampleData.findIndex((d: any) => d.time === param.time);
          const prevClose = currentIndex > 0 ? sampleData[currentIndex - 1].close : -1;

          setPriceData({
            open: data.open,
            high: data.high,
            low: data.low,
            close: data.close,
            prevClose: prevClose,
          });
        }
      }
      // Fallback: try seriesPrices (different API version)
      else if (param.seriesPrices && param.seriesPrices.size > 0) {
        const prices = param.seriesPrices.get(candlestickSeries);

        if (prices && prices.open !== undefined) {
          const currentIndex = sampleData.findIndex((d: any) => d.time === param.time);
          const prevClose = currentIndex > 0 ? sampleData[currentIndex - 1].close : -1;

          setPriceData({
            open: prices.open,
            high: prices.high,
            low: prices.low,
            close: prices.close,
            prevClose: prevClose,
          });
        }
      }
    });

    // Make chart responsive
    const handleResize = () => {
      if (chartContainerRef.current && chartRef.current) {
        chartRef.current.applyOptions({
          width: chartContainerRef.current.clientWidth,
        });
      }
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (chartRef.current) {
        chartRef.current.remove();
      }
    };
  }, [width, height]);

  // Calculate change and percentage
  const change = priceData.close - priceData.prevClose;
  const changePercent = priceData.prevClose > 0
    ? ((change / priceData.prevClose) * 100)
    : 0;
  const isPositive = change >= 0;

  // Determine if current candle is bullish (green) or bearish (red)
  const isBullish = priceData.close >= priceData.open;

  return (
    <div className="trading-chart-wrapper">
      {/* TradingView-style header */}
      <div className="trading-chart-header">
        <div className="symbol-info">
          <span className="symbol-name">{symbol}</span>
          <span className="symbol-timeframe">{timeframe}</span>
          <span className="symbol-exchange">{exchange}</span>
        </div>
        <div className="price-info">
          <span className="price-label">O</span>
          <span className={`price-value ${isBullish ? 'bullish' : 'bearish'}`}>{priceData.open.toFixed(2)}</span>

          <span className="price-label">H</span>
          <span className={`price-value ${isBullish ? 'bullish' : 'bearish'}`}>{priceData.high.toFixed(2)}</span>

          <span className="price-label">L</span>
          <span className={`price-value ${isBullish ? 'bullish' : 'bearish'}`}>{priceData.low.toFixed(2)}</span>

          <span className="price-label">C</span>
          <span className={`price-value ${isBullish ? 'bullish' : 'bearish'}`}>{priceData.close.toFixed(2)}</span>

          <span className={`price-change ${isPositive ? 'positive' : 'negative'}`}>
            {isPositive ? '+' : ''}{change.toFixed(2)} ({isPositive ? '+' : ''}{changePercent.toFixed(2)}%)
          </span>
        </div>
      </div>

      {/* Chart container */}
      <div ref={chartContainerRef} className="trading-chart-container" />
    </div>
  );
};

export default TradingChart;
